import { generatePath } from 'react-router-dom'
import { Document, Page, Text, Image, Font, StyleSheet, View, Link } from '@react-pdf/renderer'

import { getStepData } from 'UI/Components'

import { PlaybookType, QuickGuiddeType } from 'app/types'
import { paths } from 'app/paths'

// value param it a px which converts to PDF "pt" unit of measurement, 1pt = 1.333px
const pxToPt = (value: number) => Number(value / 1.333).toFixed(2)

const greyColor = 'rgba(9, 12, 16, 0.6)'

const textStyles = {
    fontSize: pxToPt(14),
    lineHeight: 1.15
}

const styles = StyleSheet.create({
    image: {
        marginVertical: 15,
        minHeight: pxToPt(220),
        borderRadius: 12,
        overflow: 'hidden',
        objectFit: 'fill'
    },
    header: {
        fontSize: pxToPt(32),
        fontWeight: 600
    },
    row: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row',
        alignItems: 'center'
    },
    divider: {
        backgroundColor: '#E0E0E0',
        height: 1,
        width: '100%',
        marginVertical: pxToPt(20)
    }
})

Font.register({
    family: 'Roboto',
    src: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu7GxKKTU1Kvnz.woff2'
})

type StyledImageProps = {
    src?: string
}

const StyledImage = ({ src }: StyledImageProps) => <Image src={src} style={styles.image} />

type Props = {
    onRender: () => void
    playbook: PlaybookType
    favIcon: string | null
    brandKitHighlightColor?: string
}

// WARNING !!!!

// when you are  trying change/add something here - do not forget
// to check if after your changes `Steps` component - looks the in the same way.
// these two components must be synchronized with each other and should have the same view.

// path to `Steps` - src/UI/Components/QuickGuiddeSteps/Steps
export const PdfDocument = ({ playbook, favIcon, onRender, brandKitHighlightColor }: Props) => {
    const path = generatePath(playbook.isPlaylist ? paths.publicPlaylist : paths.publicPlaybook, {
        playbookId: playbook.id
    })

    // use the new image with play button if exists
    // only the first step has this field
    const imageUrl = playbook.steps[0]?.docCoverScreenshot || playbook.gifUrl

    const playbookLink = window.location.origin + path

    const cover = playbook.steps.find(step => step.kind === 'cover') as QuickGuiddeType | undefined

    const coverAvatarUrl =
        cover?.audioNote?.type === 'textToSpeech'
            ? cover?.audioNote?.speakerConfig?.avatarUrlCompressed
            : ''

    return (
        <Document onRender={onRender}>
            <Page
                size="A4"
                wrap
                style={{
                    paddingVertical: '2.97cm',
                    paddingHorizontal: '2.1cm'
                }}
            >
                <View style={styles.row}>
                    {favIcon && (
                        <Image
                            src={favIcon}
                            style={{
                                minHeight: pxToPt(32),
                                width: pxToPt(32)
                            }}
                        />
                    )}
                    <View style={{ width: pxToPt(16) }} />
                    <Text style={styles.header}>{playbook.title}</Text>
                </View>
                <View style={{ marginTop: pxToPt(8) }} />
                <PoweredByGuidde />

                <Link src={playbookLink}>
                    <StyledImage src={imageUrl} />
                </Link>

                {playbook.description && (
                    <>
                        <Text
                            style={{
                                fontSize: pxToPt(12),
                                color: '#828282'
                            }}
                        >
                            {playbook.description.trim()}
                        </Text>

                        <View style={styles.divider} />
                    </>
                )}

                {cover && (
                    <>
                        <View
                            style={{
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: '100%',
                                display: 'flex'
                            }}
                        >
                            {cover.audioNote?.isIncludeTranscriptInDoc && (
                                <View wrap>
                                    {coverAvatarUrl && (
                                        <Image
                                            src={coverAvatarUrl}
                                            style={{
                                                width: 32,
                                                height: 32,
                                                marginRight: 16
                                            }}
                                        />
                                    )}

                                    <Text
                                        wrap
                                        style={{
                                            ...textStyles,
                                            color: greyColor
                                        }}
                                    >
                                        {cover.audioNote.text}
                                    </Text>
                                </View>
                            )}
                            {cover.note && (
                                <Text
                                    style={{
                                        marginVertical: pxToPt(16),
                                        ...textStyles,
                                        color: greyColor
                                    }}
                                >
                                    {cover.note}
                                </Text>
                            )}
                        </View>
                    </>
                )}

                {playbook.steps?.map((step, idx) => {
                    const { hasBgImage, stepAvatar, stepTitle, stepGoTo, stepUrlChange } =
                        getStepData(step)

                    const showGoTo = idx === (playbook.steps[0]?.kind === 'cover' ? 1 : 0)

                    if (step.kind === 'cover' || step.kind === 'end') {
                        const avatarUrl =
                            step.audioNote?.type === 'textToSpeech'
                                ? step.audioNote.speakerConfig.avatarUrlCompressed
                                : ''

                        if (step.kind === 'cover') return null

                        return (
                            <View key={step.id + idx}>
                                {step.audioNote?.isIncludeTranscriptInDoc && (
                                    <>
                                        {avatarUrl && (
                                            <Image
                                                src={avatarUrl}
                                                style={{
                                                    marginRight: pxToPt(16),
                                                    width: pxToPt(32),
                                                    height: pxToPt(32)
                                                }}
                                            />
                                        )}

                                        <Text
                                            wrap
                                            style={{
                                                ...textStyles,
                                                color: greyColor
                                            }}
                                        >
                                            {step.audioNote.text}
                                        </Text>
                                    </>
                                )}
                                {step.note && (
                                    <Text
                                        style={{
                                            marginVertical: pxToPt(16),
                                            ...textStyles,
                                            color: greyColor
                                        }}
                                    >
                                        {step.note}
                                    </Text>
                                )}
                                <View style={{ marginTop: 5 }}>
                                    <PoweredByGuidde />
                                </View>
                            </View>
                        )
                    }

                    return (
                        <View key={step.id + idx}>
                            {showGoTo && stepGoTo.host && (
                                <Text
                                    style={{
                                        fontWeight: 500,
                                        ...textStyles,
                                        marginVertical: pxToPt(2)
                                    }}
                                >
                                    Go to{' '}
                                    <Link
                                        src={stepGoTo.href}
                                        style={{
                                            color: '#2D9CDB',
                                            textDecoration: 'none',
                                            fontWeight: 500,
                                            ...textStyles
                                        }}
                                    >
                                        {stepGoTo.host}
                                    </Link>
                                </Text>
                            )}

                            <View
                                wrap
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    marginVertical: pxToPt(16)
                                }}
                            >
                                <View
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        backgroundColor: brandKitHighlightColor,
                                        width: pxToPt(24),
                                        height: pxToPt(24),
                                        color: '#000000',
                                        borderRadius: '50%',
                                        overflow: 'hidden',
                                        marginRight: pxToPt(10)
                                    }}
                                >
                                    <Text
                                        style={{
                                            fontSize: pxToPt(12)
                                        }}
                                    >
                                        {idx.toString().padStart(2, '0')}
                                    </Text>
                                </View>
                                <Text
                                    style={{
                                        fontSize: pxToPt(20),
                                        fontWeight: 500,
                                        marginTop: pxToPt(1.5)
                                    }}
                                >
                                    {stepTitle}
                                </Text>
                            </View>
                            <View>
                                {stepUrlChange && (
                                    <Link
                                        src={`${step.url.includes('http') ? '' : '//'}${step.url}`}
                                    >
                                        <Text
                                            style={{
                                                overflow: 'hidden',
                                                height: pxToPt(30),
                                                fontSize: pxToPt(20),
                                                textOverflow: 'ellipsis'
                                            }}
                                        >
                                            {stepUrlChange}
                                        </Text>
                                    </Link>
                                )}
                            </View>

                            {step.audioNote?.isIncludeTranscriptInDoc && step.audioNote.text && (
                                <View
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <Image
                                        src={stepAvatar}
                                        style={{
                                            width: pxToPt(40),
                                            height: pxToPt(40),
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            objectFit: 'fill'
                                        }}
                                    />

                                    <Text
                                        wrap
                                        style={{
                                            width: `calc(100% - ${pxToPt(56)})`,
                                            marginLeft: pxToPt(16),
                                            ...textStyles,
                                            color: greyColor
                                        }}
                                    >
                                        {step.audioNote.text}
                                    </Text>
                                </View>
                            )}

                            {step.note && (
                                <Text
                                    style={{
                                        ...textStyles,
                                        color: greyColor,
                                        marginBottom: pxToPt(16)
                                    }}
                                >
                                    {step.note}
                                </Text>
                            )}

                            {hasBgImage && (
                                <StyledImage
                                    src={`${
                                        step.docScreenshot || step.drawnScreenshot
                                    }&time=${new Date()}`}
                                />
                            )}
                        </View>
                    )
                })}
            </Page>
        </Document>
    )
}

const PoweredByGuidde = () => (
    <Text>
        <Text style={textStyles}>Powered by </Text>
        <Link src="https://guidde.com">
            <Text style={{ color: '#cb0000', ...textStyles }}>Guidde</Text>
        </Link>
    </Text>
)
